import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

type VerificationStatus = 'verifying' | 'success' | 'pending' | 'error';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<VerificationStatus>('verifying');
  const [attempts, setAttempts] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const sessionId = searchParams.get('session_id');

  const MAX_ATTEMPTS = 15; // ~45 segundos (15 x 3s)
  const POLL_INTERVAL = 3000; // 3 segundos

  const checkSubscription = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription', {});
      
      if (error) {
        console.error('[PaymentSuccess] Erro check-subscription:', error);
        return { success: false, subscribed: false };
      }

      
      return { success: true, subscribed: data?.subscribed === true };
    } catch (err) {
      console.error('[PaymentSuccess] Exceção check-subscription:', err);
      return { success: false, subscribed: false };
    }
  }, []);

  useEffect(() => {
    let pollTimer: NodeJS.Timeout | null = null;
    let mounted = true;

    const pollSubscription = async () => {
      if (!mounted) return;

      const result = await checkSubscription();
      
      if (!mounted) return;

      if (result.subscribed) {
        setStatus('success');
        // Redirecionar automaticamente após 3 segundos
        setTimeout(() => {
          if (mounted) {
            navigate('/portal');
          }
        }, 3000);
        return;
      }

      setAttempts(prev => prev + 1);

      if (attempts >= MAX_ATTEMPTS) {
        setStatus('pending');
        return;
      }

      // Continuar polling
      pollTimer = setTimeout(pollSubscription, POLL_INTERVAL);
    };

    // Iniciar polling
    pollSubscription();

    return () => {
      mounted = false;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [checkSubscription, navigate, attempts]);

  const handleRetry = () => {
    setAttempts(0);
    setStatus('verifying');
  };

  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-lg font-medium mb-2">Confirmando pagamento...</p>
            <p className="text-sm text-muted-foreground">
              Aguarde enquanto verificamos sua assinatura.
            </p>
            {attempts > 3 && (
              <p className="text-xs text-muted-foreground mt-4">
                Tentativa {attempts} de {MAX_ATTEMPTS}...
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <CardTitle className="text-2xl">Processando Pagamento</CardTitle>
            <CardDescription className="text-base">
              Seu pagamento foi recebido, mas a confirmação ainda está sendo processada.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground mb-2">
                Isso pode levar alguns minutos. Você receberá um email de confirmação.
              </p>
              <p className="text-sm text-muted-foreground">
                Se a situação persistir, entre em contato com o suporte.
              </p>
            </div>

            <div className="space-y-2">
              <Button 
                className="w-full gap-2" 
                onClick={handleRetry}
              >
                <RefreshCw className="h-4 w-4" />
                Verificar Novamente
              </Button>
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={() => navigate('/portal')}
              >
                Ir para o Portal
              </Button>
              <Button 
                variant="ghost" 
                className="w-full" 
                onClick={() => window.location.href = 'mailto:suporte@simpledesk.com.br'}
              >
                Contatar Suporte
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <CardTitle className="text-2xl">Erro na Verificação</CardTitle>
            <CardDescription className="text-base">
              {errorMessage || 'Não foi possível verificar o status do pagamento.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Button 
                className="w-full gap-2" 
                onClick={handleRetry}
              >
                <RefreshCw className="h-4 w-4" />
                Tentar Novamente
              </Button>
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={() => navigate('/portal')}
              >
                Ir para o Portal
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // status === 'success'
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <CardTitle className="text-2xl">Pagamento Confirmado!</CardTitle>
          <CardDescription className="text-base">
            Sua assinatura foi ativada com sucesso.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-lg p-4 text-center">
            <p className="text-sm text-green-700 dark:text-green-300 mb-2">
              Você será redirecionado automaticamente em instantes...
            </p>
          </div>

          <div className="space-y-2">
            <Button 
              className="w-full" 
              onClick={() => navigate('/portal')}
            >
              Ir para o Portal
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
